import React, { useState } from "react";
import { Container, Row, Col, Card, Form, Button, Alert } from "react-bootstrap";
import API_BASE_URL from "../config/api";

const Register = () => {
  const [formData, setFormData] = useState({
    name: "",
    mobileNumber: "",
    whatsappNumber: "",
    qualification: "",
    yearOfPassout: "",
    workExperience: "",
    referenceName: "",
    referenceMobile: "",
    course: ""
  });

  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertVariant, setAlertVariant] = useState("success");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // ---------- Mandatory Field Validation ----------
    if (
      !formData.name ||
      !formData.mobileNumber ||
      !formData.course ||
      !formData.whatsappNumber ||
      !formData.workExperience ||
      !formData.referenceName ||
      !formData.referenceMobile
    ) {
      setAlertMessage("Please fill in all required fields.");
      setAlertVariant("danger");
      setShowAlert(true);
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(formData)
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Submission failed");
      }

      setAlertMessage(result.message || "Registration submitted successfully! We will contact you soon.");
      setAlertVariant("success");
      setShowAlert(true);

      // Reset form
      setFormData({
        name: "",
        mobileNumber: "",
        whatsappNumber: "",
        qualification: "",
        yearOfPassout: "",
        workExperience: "",
        referenceName: "",
        referenceMobile: "",
        course: ""
      });

    } catch (error) {
      console.error("Error submitting registration:", error);
      setAlertMessage(error.message || "Submission failed. Please try again.");
      setAlertVariant("danger");
      setShowAlert(true);
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setShowAlert(false), 5000);
    }
  };

  return (
    <Container className="page-shell register-page my-5">
      <Row className="justify-content-center">
        <Col xs={12} md={10} lg={11}>
          <Card className="form-card border-0 registration-shell">
            <Card.Body className="p-0">
              <div className="registration-layout">
                <div className="registration-side-panel">
                  <div className="registration-panel-content">
                    <span className="eyebrow dark">Admission Open</span>
                    <h2>Take the next step in your tech career.</h2>
                    <p>
                      Join a learning experience built for practical, job-ready skills in DevOps, Full-Stack development, and Automation Testing.
                    </p>

                    <ul className="registration-benefits">
                      <li>Live mentorship from industry professionals</li>
                      <li>Project-driven training and placement support</li>
                      <li>Flexible cohort learning with personalized guidance</li>
                    </ul>
                  </div>
                </div>

                <div className="registration-form-panel">
                  <div className="text-center mb-4 page-header registration-header">
                    <span className="eyebrow dark">Register</span>
                    <h1 className="fw-bold mb-2">Registration Form</h1>
                    <p className="page-subtitle mb-0 mx-auto">
                      Fill in the details below to start your journey.
                    </p>
                  </div>

                  {showAlert && (
                    <Alert variant={alertVariant} dismissible onClose={() => setShowAlert(false)}>
                      {alertMessage}
                    </Alert>
                  )}

                  <Form onSubmit={handleSubmit} className="registration-form">
                    <Row className="g-4">
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>
                            Name <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Control
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                          />
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>
                            Mobile Number <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Control
                            type="tel"
                            name="mobileNumber"
                            value={formData.mobileNumber}
                            onChange={handleChange}
                            required
                          />
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>
                            WhatsApp Number <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Control
                            type="tel"
                            name="whatsappNumber"
                            value={formData.whatsappNumber}
                            onChange={handleChange}
                            required
                          />
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>
                            Qualification <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Control
                            type="text"
                            name="qualification"
                            value={formData.qualification}
                            onChange={handleChange}
                            required
                          />
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>
                            Year Of Passout <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Control
                            type="text"
                            name="yearOfPassout"
                            value={formData.yearOfPassout}
                            onChange={handleChange}
                            required
                          />
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>
                            Course <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Select
                            name="course"
                            value={formData.course}
                            onChange={handleChange}
                            required
                          >
                            <option value="">Select a course</option>
                            <option value="DevOps & Cloud Automation">DevOps & Cloud Automation</option>
                            <option value="Full-Stack MERN Development">Full-Stack MERN Development</option>
                            <option value="Automation Testing">Automation Testing</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>

                      <Col xs={12}>
                        <Form.Group>
                          <Form.Label>
                            Previous Work Experience <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={4}
                            name="workExperience"
                            value={formData.workExperience}
                            onChange={handleChange}
                            required
                          />
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>
                            Reference Name <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Control
                            type="text"
                            name="referenceName"
                            value={formData.referenceName}
                            onChange={handleChange}
                            required
                          />
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>
                            Reference Mobile Number <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Control
                            type="tel"
                            name="referenceMobile"
                            value={formData.referenceMobile}
                            onChange={handleChange}
                            required
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <div className="registration-actions mt-4">
                      <Button type="submit" size="lg" disabled={isSubmitting}>
                        {isSubmitting ? "Submitting..." : "Submit Registration"}
                      </Button>
                    </div>
                  </Form>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default Register;
